import { Link } from 'react-router-dom';
import { articles } from '@/data/articleIndex';
import { ArrowRight } from 'lucide-react';
import { getEnglishContent } from '@/types';

const categoryLabels: Record<string, string> = {
  'mobile-living': 'Van & Mobile Living',
  'off-grid-homes': 'Off-Grid Homes',
  'boat-living': 'Boat Living',
  'camping': 'Camping & Outdoors'
};

const recommendedArticles: Record<string, string[]> = {
  'mobile-living': [
    'van-life-for-beginners',
    'how-to-choose-your-first-van-or-bus',
    'hidden-costs-of-vanlife'
  ],
  'off-grid-homes': [
    'personal-tips-off-grid',
    'off-grid-power-solar-battery-guide',
    'building-your-own-tiny-house'
  ],
  'boat-living': [
    'living-on-a-boat-full-time-guide',
    'what-to-consider-before-buying-a-liveaboard-boat',
    'personal-tips-boat-living'
  ],
  'camping': [
    'personal-tips-camping',
    'what-to-consider-before-your-first-camping-trip',
    'winter-camping-essentials'
  ]
};

export const StartHereSection = () => {
  const categoryIds = ['mobile-living', 'off-grid-homes', 'boat-living', 'camping'];

  return (
    <section className="py-16 md:py-24 bg-secondary/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-primary mb-4">
            Start Here: Best Guides
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Our most helpful articles to begin your alternative living journey
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 max-w-5xl mx-auto border border-border/40">
          {categoryIds.map((categoryId, categoryIndex) => {
            const recommendedSlugs = recommendedArticles[categoryId] || [];
            
            const articlesToShow = recommendedSlugs
              .map(slug => articles.find(a => a.slug === slug))
              .filter(Boolean)
              .slice(0, 3);

            const categoryName = categoryLabels[categoryId] || categoryId;

            return (
              <div 
                key={categoryId}
                className="relative p-8 md:p-10 animate-fade-in border-border/40"
                style={{ 
                  animationDelay: `${categoryIndex * 100}ms`,
                  borderRight: categoryIndex % 2 === 0 ? '1px solid hsl(var(--border) / 0.4)' : 'none',
                  borderBottom: categoryIndex < 2 ? '1px solid hsl(var(--border) / 0.4)' : 'none'
                }}
              >
                {/* Category number */}
                <span className="text-xs font-light tracking-[0.3em] text-muted-foreground uppercase mb-4 block">
                  0{categoryIndex + 1}
                </span>

                <h3 className="font-serif text-xl font-medium text-primary mb-6">
                  {categoryName}
                </h3>
                <ul className="space-y-3">
                  {articlesToShow.map((article) => {
                    if (!article) return null;
                    const content = getEnglishContent(article);
                    return (
                      <li key={article.id}>
                        <Link 
                          to={`/article/${article.slug}`}
                          className="group flex items-start gap-3 text-foreground/80 hover:text-accent transition-colors duration-500"
                        >
                          <ArrowRight className="w-4 h-4 mt-1 flex-shrink-0 text-accent/40 group-hover:text-accent group-hover:translate-x-1 transition-all duration-500" />
                          <span className="leading-snug font-light">{content.title}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
                <Link 
                  to={`/category/${categoryId}`}
                  className="inline-flex items-center mt-6 text-[10px] tracking-luxury uppercase text-accent/80 hover:text-accent font-normal transition-colors duration-500"
                >
                  View all →
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
